import React, { useRef } from "react";
import { FormikProps } from "formik";
import {
  getPriceBySubmarket,
  getPriceBySubmarketFromMatrix,
  getPriceCurvesForYears,
} from "../../../utils/EnergyPrice";
import {
  Distribuidora,
  EnergyPriceMatrix,
  EnergyPriceCurve,
  FormValues,
  SituacaoUsina,
  SimulationHistoryItem,
} from "../types";
import { CAMPOS_OCULTOS_POR_SITUACAO } from "../situacaoUsina";
import { getRestoredHistoryValue } from "../restoreHistoryItem";

type ValueChange = { target: { value: string } };

const hasValidPriceCurve = (
  priceData: EnergyPriceCurve | null
): priceData is EnergyPriceCurve => {
  if (!priceData) return false;

  return (
    typeof priceData.i0Atacadista === "string" &&
    priceData.i0Atacadista.length > 0 &&
    typeof priceData.i0Varejista === "string" &&
    priceData.i0Varejista.length > 0 &&
    typeof priceData.i5Atacadista === "string" &&
    priceData.i5Atacadista.length > 0 &&
    typeof priceData.i5Varejista === "string" &&
    priceData.i5Varejista.length > 0 &&
    typeof priceData.i1Atacadista === "string" &&
    priceData.i1Atacadista.length > 0 &&
    typeof priceData.i1Varejista === "string" &&
    priceData.i1Varejista.length > 0
  );
};

export const useSimulationFieldHandlers = ({
  formik,
  distribuidoras,
  energyPriceMatrix,
}: {
  formik: FormikProps<FormValues>;
  distribuidoras: Distribuidora[];
  energyPriceMatrix: EnergyPriceMatrix | null;
}) => {
  const tipoContratacao = formik.values.tipoContratacao;
  const desconto = formik.values.desconto;
  const preserveGenerationOnIndicativeUpdate = useRef(false);

  const getSuggestedPrices = (
    uf: string,
    priceKey: keyof EnergyPriceCurve,
    initialYear = formik.values.initialYear,
    quantidadeAnos = formik.values.quantidadeAnos
  ) => {
    const pricesByYear = getPriceBySubmarketFromMatrix(uf, energyPriceMatrix);
    const years = Array.from(
      { length: quantidadeAnos },
      (_, index) => initialYear + index
    );
    const dynamicPrices = getPriceCurvesForYears(pricesByYear, years);

    if (dynamicPrices.every(hasValidPriceCurve))
      return dynamicPrices.map((price: EnergyPriceCurve) => price[priceKey]);

    // Fallback para manter compatibilidade enquanto o endpoint estabiliza.
    const fallbackPrices = getPriceBySubmarket(uf);
    if (!fallbackPrices) return null;

    return years.map(
      (_, index) => fallbackPrices[priceKey][index >= 5 ? 4 : index]
    );
  };

  const changeGenerationEnergyPrice = (
    tipoContratacao = formik.values.tipoContratacao,
    uf = formik.values.uf,
    initialYear = formik.values.initialYear,
    quantidadeAnos = formik.values.quantidadeAnos
  ) => {
    preserveGenerationOnIndicativeUpdate.current = false;
    const priceKey =
      tipoContratacao === "atacado" ? "i5Atacadista" : "i5Varejista";
    const prices = getSuggestedPrices(
      uf,
      priceKey,
      initialYear,
      quantidadeAnos
    );
    if (prices) formik.setFieldValue("precoEnergiaGeracao", prices);
  };

  const restoreSimulationHistory = (item: SimulationHistoryItem) => {
    const values = Object.entries(item).reduce<FormValues>(
      (restored, [key, value]) => ({
        ...restored,
        [key]: getRestoredHistoryValue(key, value),
      }),
      { ...formik.values }
    );
    values.precoPadrao = false;
    preserveGenerationOnIndicativeUpdate.current =
      Number(values.desconto) === 0.5;
    values.uf =
      (typeof item.uf === "string" && item.uf) ||
      distribuidoras.find(
        (distribuidora) =>
          distribuidora.value === values.distribuidora ||
          distribuidora.name === values.distribuidora
      )?.uf ||
      "";

    // I0/I1 antigos podem ter salvo a curva de compra como geração.
    // Em I5, preservamos a geração salva e suas possíveis edições manuais.
    if (Number(values.desconto) !== 0.5) {
      const prices = getSuggestedPrices(
        values.uf,
        values.tipoContratacao === "atacado" ? "i5Atacadista" : "i5Varejista",
        Number(values.initialYear),
        Number(values.quantidadeAnos)
      );
      if (prices) values.precoEnergiaGeracao = prices;
    }
    formik.setValues(values);
  };

  const changeEnergyPrice = (
    tipoContratacao: string,
    desconto: number,
    uf = formik.values.uf,
    initialYear = formik.values.initialYear,
    quantidadeAnos = formik.values.quantidadeAnos
  ) => {
    let energyPriceKey: keyof EnergyPriceCurve =
      tipoContratacao === "atacado" ? "i0Atacadista" : "i0Varejista";
    if (desconto === 0.5)
      energyPriceKey =
        tipoContratacao === "atacado" ? "i5Atacadista" : "i5Varejista";
    else if (desconto === 1)
      energyPriceKey =
        tipoContratacao === "atacado" ? "i1Atacadista" : "i1Varejista";

    const prices = getSuggestedPrices(
      uf,
      energyPriceKey,
      initialYear,
      quantidadeAnos
    );
    if (prices) formik.setFieldValue("precoEnergia", prices);
  };

  const changeTipoContratacao = (e: ValueChange) => {
    const { value } = e.target;

    if (value !== tipoContratacao) {
      formik.setFieldValue("tipoContratacao", value);
      formik.setFieldValue("precoPadrao", true);

      changeEnergyPrice(value, desconto);
      changeGenerationEnergyPrice(value);
    }
  };

  const refreshIndicativePrices = () => {
    if (formik.values.precoPadrao) changeEnergyPrice(tipoContratacao, desconto);
    if (!preserveGenerationOnIndicativeUpdate.current)
      changeGenerationEnergyPrice();
  };

  const calculateEnergyPrice = (e: ValueChange) => {
    const { value } = e.target;

    formik.setFieldValue("desconto", parseFloat(value));
    formik.setFieldValue("precoPadrao", true);

    changeEnergyPrice(tipoContratacao, parseFloat(value));
  };

  const handleDefaultPrices = (_e: React.ChangeEvent<HTMLInputElement>) => {
    const precoPadrao = formik.values.precoPadrao;

    formik.setFieldValue("precoPadrao", !precoPadrao);

    if (!precoPadrao) changeEnergyPrice(tipoContratacao, desconto);
  };

  const resetGrupoTarifario = (e: ValueChange) => {
    const { value } = e.target;

    let uf;

    for (let i in distribuidoras) {
      if (distribuidoras[i].name === value) {
        uf = distribuidoras[i].uf;
        continue;
      }
    }

    formik.setFieldValue("grupoTarifario", "");
    formik.setFieldValue("uf", uf);
    formik.setFieldValue("distribuidora", value);
    formik.setFieldValue("precoPadrao", true);

    changeEnergyPrice(tipoContratacao, desconto, uf);
    changeGenerationEnergyPrice(tipoContratacao, uf);
  };

  const resetTHSWithGrupoTarifario = (e: ValueChange) => {
    const { value } = e.target;
    if (value === "A3") formik.setFieldValue("ths", "AZUL");
    formik.setFieldValue("grupoTarifario", value);
  };

  const changeContractTypeWithDemandaForaPonta = (e: ValueChange) => {
    const { value } = e.target;

    formik.setFieldValue("demandaForaPonta", value);
  };

  const changeContractTypeWithDemandaPonta = (e: ValueChange) => {
    const { value } = e.target;

    formik.setFieldValue("demandaPonta", value);
  };

  const resetDiscounWithtUltrapassagemForaPontaType = (e: ValueChange) => {
    const { value } = e.target;
    formik.setFieldValue("demandaForaPontaUltrapassagem", value);
    // formik.setFieldValue("desconto", -1);
  };

  const resetDiscountWithUltrapassagemPontaType = (e: ValueChange) => {
    const { value } = e.target;
    formik.setFieldValue("demandaPontaUltrapassagem", value);
    // formik.setFieldValue("desconto", -1);
  };

  const resetSimultaneidade = (_e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("simultaneidade", !formik.values.local ? 65 : 0);
    formik.setFieldValue("local", !formik.values.local);
  };

  const changeSituacaoUsina = (e: ValueChange) => {
    const situacao = e.target.value as SituacaoUsina;
    formik.setFieldValue("situacaoUsina", situacao);
    // Os campos escondidos pela situação também saem do payload; zerá-los evita
    // que um valor digitado antes fique preso no formulário e no histórico.
    CAMPOS_OCULTOS_POR_SITUACAO[situacao].forEach((campo) => {
      formik.setFieldValue(campo, campo === "simultaneidade" ? 0 : "0");
    });
  };

  const resetTHS = (e: ValueChange) => {
    const { value } = e.target;
    formik.setFieldValue("demandaPonta", "0");
    formik.setFieldValue("demandaPontaUltrapassagem", "0");
    formik.setFieldValue("ths", value);
  };

  const changeSimulationPeriod = (
    initialYear: number,
    quantidadeAnos: number
  ) => {
    formik.setFieldValue("initialYear", initialYear);
    formik.setFieldValue("quantidadeAnos", quantidadeAnos);
    changeGenerationEnergyPrice(
      tipoContratacao,
      formik.values.uf,
      initialYear,
      quantidadeAnos
    );

    if (formik.values.precoPadrao) {
      changeEnergyPrice(
        tipoContratacao,
        desconto,
        formik.values.uf,
        initialYear,
        quantidadeAnos
      );
      return;
    }

    const prices = formik.values.precoEnergia;
    formik.setFieldValue(
      "precoEnergia",
      Array.from(
        { length: quantidadeAnos },
        (_, index) => prices[index] ?? prices[prices.length - 1] ?? ""
      )
    );
  };

  const resetEnergyPrice = (value: string, index: number) => {
    const prices = [...formik.values.precoEnergia];
    prices[index] = value;
    formik.setFieldValue("precoEnergia", prices);
    if (desconto === 0.5) {
      preserveGenerationOnIndicativeUpdate.current = true;
      const generationPrices = [...formik.values.precoEnergiaGeracao];
      generationPrices[index] = value;
      formik.setFieldValue("precoEnergiaGeracao", generationPrices);
    }
  };

  // const resetCovid = (e) => {
  //   formik.setFieldValue("isTaxCovid", !formik.values.isTaxCovid);
  // };

  const resetScarcity = (_e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("isTaxScarcity", !formik.values.isTaxScarcity);
  };

  const resetEncargos = (_e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("isEncargos", !formik.values.isEncargos);
  };

  const resetAbateICMS = (_e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("abateICMS", !formik.values.abateICMS);
  };

  const resetGenerationConsume = (e: ValueChange) => {
    const { value } = e.target;
    formik.setFieldValue("consumoGerador", value);
    formik.setFieldValue(
      "custoGerador",
      (
        Number(value.replace(",", ".")) *
        Number(formik.values.custoDiesel.replace(",", "."))
      )
        .toFixed(2)
        .replace(".", ",")
    );
  };

  const resetGenerationCost = (e: ValueChange) => {
    const { value } = e.target;
    formik.setFieldValue("custoGerador", value);
    formik.setFieldValue(
      "consumoGerador",
      (
        Number(value.replace(",", ".")) /
        Number(formik.values.custoDiesel.replace(",", "."))
      )
        .toFixed(2)
        .replace(".", ",")
    );
  };

  const resetDieselCost = (e: ValueChange) => {
    const { value } = e.target;
    formik.setFieldValue("custoDiesel", value);
    formik.setFieldValue("custoGerador", "");
    formik.setFieldValue("consumoGerador", "");
  };

  const addItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      {
        nome: "",
        potencia: "",
        geradorFotovoltaico: "",
        projetoHomologacao: "",
        epc: "",
      },
    ]);
  };

  const removeItem = (index: number) => {
    const newItems = formik.values.items.filter((_, i: number) => i !== index);
    formik.setFieldValue("items", newItems);
  };

  const handleItemChange = (
    index: number,
    event: { target: { name: string; value: string } }
  ) => {
    const { name, value } = event.target;
    const newItems = [...formik.values.items];
    (newItems[index] as any)[name] = value;
    formik.setFieldValue("items", newItems);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    path: string
  ) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      formik.setFieldValue(path, selectedFile);
    }
  };

  return {
    refreshIndicativePrices,
    restoreSimulationHistory,
    changeEnergyPrice,
    changeTipoContratacao,
    calculateEnergyPrice,
    handleDefaultPrices,
    resetGrupoTarifario,
    resetTHSWithGrupoTarifario,
    changeContractTypeWithDemandaForaPonta,
    changeContractTypeWithDemandaPonta,
    resetDiscounWithtUltrapassagemForaPontaType,
    resetDiscountWithUltrapassagemPontaType,
    resetSimultaneidade,
    changeSituacaoUsina,
    resetTHS,
    changeSimulationPeriod,
    resetEnergyPrice,
    resetScarcity,
    resetEncargos,
    resetAbateICMS,
    resetGenerationConsume,
    resetGenerationCost,
    resetDieselCost,
    addItem,
    removeItem,
    handleItemChange,
    handleFileChange,
  };
};
